/**
 * Song Upload Page
 * Upload and sell songs on the marketplace
 */

import { useState, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../context/AuthContext';
import {
  Upload,
  Music,
  Image,
  DollarSign,
  Clock,
  Tag,
  FileAudio,
  X,
  Check,
  AlertCircle,
  Loader2,
  ChevronLeft,
  Sparkles,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Genre options
const GENRES = [
  'Hip Hop', 'R&B', 'Pop', 'Rock', 'Electronic', 'EDM',
  'Trap', 'Lo-Fi', 'Jazz', 'Classical', 'Country', 'Reggae', 'Latin', 'Other'
];

// Musical keys
const KEYS = [
  'C Major', 'C Minor', 'C# Major', 'C# Minor',
  'D Major', 'D Minor', 'D# Major', 'D# Minor',
  'E Major', 'E Minor',
  'F Major', 'F Minor', 'F# Major', 'F# Minor',
  'G Major', 'G Minor', 'G# Major', 'G# Minor',
  'A Major', 'A Minor', 'A# Major', 'A# Minor',
  'B Major', 'B Minor'
];

// License types
const LICENSE_TYPES = [
  { id: 'lease', name: 'Lease License', description: 'Non-exclusive, limited use', multiplier: 1 },
  { id: 'standard', name: 'Standard License', description: 'Non-exclusive, broader distribution', multiplier: 2 },
  { id: 'exclusive', name: 'Exclusive License', description: 'Full ownership, removed from marketplace', multiplier: 10 },
];

// File Upload Component
const FileUpload = ({ label, accept, file, onFileChange, icon: Icon, maxSize = '50MB' }) => {
  const inputRef = useRef(null);
  
  const handleDrop = (e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) onFileChange(droppedFile);
  };
  
  return (
    <div>
      <label className="text-sm text-zinc-400 mb-2 block">{label}</label>
      <div
        onClick={() => inputRef.current?.click()}
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
        className={`relative border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ${
          file 
            ? 'border-emerald-500/50 bg-emerald-500/10' 
            : 'border-zinc-700 hover:border-violet-500/50 hover:bg-violet-500/5'
        }`}
      >
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          onChange={(e) => onFileChange(e.target.files?.[0])}
          className="hidden"
        />
        
        {file ? (
          <div className="flex items-center justify-center gap-3">
            <Check className="w-5 h-5 text-emerald-400" />
            <span className="text-emerald-400 font-medium">{file.name}</span>
            <button
              onClick={(e) => { e.stopPropagation(); onFileChange(null); }}
              className="text-zinc-400 hover:text-white"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <>
            <Icon className="w-8 h-8 text-zinc-500 mx-auto mb-2" />
            <p className="text-zinc-400">Drop file here or click to browse</p>
            <p className="text-xs text-zinc-500 mt-1">Max size: {maxSize}</p>
          </>
        )}
      </div>
    </div>
  );
};

export default function SongUploadPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  
  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [genre, setGenre] = useState('');
  const [subgenre, setSubgenre] = useState('');
  const [bpm, setBpm] = useState('');
  const [songKey, setSongKey] = useState('');
  const [tags, setTags] = useState('');
  const [price, setPrice] = useState('');
  const [licenseType, setLicenseType] = useState('standard');
  const [stemsAvailable, setStemsAvailable] = useState(false);
  const [stemsPrice, setStemsPrice] = useState('');
  
  // Files
  const [audioFile, setAudioFile] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);
  const [coverArt, setCoverArt] = useState(null);
  const [coverArtPreview, setCoverArtPreview] = useState(null);
  
  // Handle cover art selection
  const handleCoverArtChange = (file) => {
    setCoverArt(file);
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => setCoverArtPreview(e.target.result);
      reader.readAsDataURL(file);
    } else {
      setCoverArtPreview(null);
    }
  };
  
  // Convert file to base64
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result.split(',')[1]);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };
  
  // Handle submit
  const handleSubmit = async () => {
    if (!user) {
      toast.error('Please log in to upload songs');
      return;
    }
    
    if (!title || !genre || !price || !audioFile) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setLoading(true);
    
    try {
      // Prepare form data
      const formData = new FormData();
      formData.append('title', title);
      formData.append('description', description);
      formData.append('genre', genre);
      formData.append('subgenre', subgenre);
      formData.append('bpm', bpm || '');
      formData.append('key', songKey || '');
      formData.append('price', price);
      formData.append('license_type', licenseType);
      formData.append('tags', tags);
      formData.append('stems_available', stemsAvailable.toString());
      formData.append('stems_price', stemsPrice || '0');
      formData.append('artist_id', user.id);
      formData.append('artist_name', user.name || user.email);
      
      // Convert audio file to base64
      const audioBase64 = await fileToBase64(audioFile);
      formData.append('audio_data', audioBase64);
      
      // Convert preview if provided
      if (previewFile) {
        const previewBase64 = await fileToBase64(previewFile);
        formData.append('preview_data', previewBase64);
      }
      
      // Convert cover art if provided
      if (coverArt) {
        const coverBase64 = await fileToBase64(coverArt);
        formData.append('cover_art_data', coverBase64);
      }
      
      const response = await fetch(`${API_URL}/api/songs/upload`, {
        method: 'POST',
        body: formData,
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Upload failed');
      }
      
      const data = await response.json();
      toast.success('Song uploaded successfully! It will be reviewed shortly.');
      navigate('/songs');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.message || 'Failed to upload song');
    } finally {
      setLoading(false);
    }
  };
  
  if (!user) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="text-center">
          <Music className="w-12 h-12 text-violet-400 mx-auto mb-4" />
          <p className="text-zinc-400 mb-4">Please log in to upload songs</p>
          <Button onClick={() => navigate('/login')} className="bg-violet-600 hover:bg-violet-500">
            Sign In
          </Button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      {/* Header */}
      <div className="bg-zinc-900/50 border-b border-zinc-800">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/songs')}
              className="text-zinc-400 hover:text-white"
            >
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold">Upload Your Music</h1>
              <p className="text-sm text-zinc-400">Share and sell your songs</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Progress Steps */}
      <div className="max-w-3xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-8">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center font-medium ${
                step >= s ? 'bg-violet-500 text-white' : 'bg-zinc-800 text-zinc-500'
              }`}>
                {s}
              </div>
              {s < 3 && (
                <div className={`w-24 h-1 mx-2 rounded ${
                  step > s ? 'bg-violet-500' : 'bg-zinc-800'
                }`} />
              )}
            </div>
          ))}
        </div>
        
        {/* Step 1: Basic Info */}
        {step === 1 && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-6"
          >
            <h2 className="text-lg font-semibold mb-4">Song Details</h2>
            
            <div>
              <label className="text-sm text-zinc-400 mb-2 block">Title *</label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter song title"
                className="bg-zinc-900/50 border-zinc-800"
              />
            </div>
            
            <div>
              <label className="text-sm text-zinc-400 mb-2 block">Description</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe your song..."
                rows={4}
                className="w-full bg-zinc-900/50 border border-zinc-800 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:border-violet-500"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-zinc-400 mb-2 block">Genre *</label>
                <select
                  value={genre}
                  onChange={(e) => setGenre(e.target.value)}
                  className="w-full bg-zinc-900/50 border border-zinc-800 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-violet-500"
                >
                  <option value="">Select genre</option>
                  {GENRES.map(g => (
                    <option key={g} value={g}>{g}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="text-sm text-zinc-400 mb-2 block">Subgenre</label>
                <Input
                  value={subgenre}
                  onChange={(e) => setSubgenre(e.target.value)}
                  placeholder="e.g., Boom Bap"
                  className="bg-zinc-900/50 border-zinc-800"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-zinc-400 mb-2 block">BPM</label>
                <Input
                  type="number"
                  value={bpm}
                  onChange={(e) => setBpm(e.target.value)}
                  placeholder="120"
                  min="20"
                  max="300"
                  className="bg-zinc-900/50 border-zinc-800"
                />
              </div>
              
              <div>
                <label className="text-sm text-zinc-400 mb-2 block">Key</label>
                <select
                  value={songKey}
                  onChange={(e) => setSongKey(e.target.value)}
                  className="w-full bg-zinc-900/50 border border-zinc-800 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-violet-500"
                >
                  <option value="">Select key</option>
                  {KEYS.map(k => (
                    <option key={k} value={k}>{k}</option>
                  ))}
                </select>
              </div>
            </div>
            
            <div>
              <label className="text-sm text-zinc-400 mb-2 block">Tags (comma-separated)</label>
              <Input
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                placeholder="e.g., dark, melodic, hard-hitting"
                className="bg-zinc-900/50 border-zinc-800"
              />
            </div>
            
            <Button
              onClick={() => setStep(2)}
              disabled={!title || !genre}
              className="w-full bg-violet-600 hover:bg-violet-500"
            >
              Continue
            </Button>
          </motion.div>
        )}
        
        {/* Step 2: Files */}
        {step === 2 && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-6"
          >
            <h2 className="text-lg font-semibold mb-4">Upload Files</h2>
            
            <FileUpload
              label="Main Audio File (WAV/MP3) *"
              accept=".wav,.mp3"
              file={audioFile}
              onFileChange={setAudioFile}
              icon={FileAudio}
              maxSize="100MB"
            />
            
            <FileUpload
              label="Preview Clip (30 seconds, optional)"
              accept=".wav,.mp3"
              file={previewFile}
              onFileChange={setPreviewFile}
              icon={Music}
              maxSize="10MB"
            />
            
            <div>
              <label className="text-sm text-zinc-400 mb-2 block">Cover Art (optional)</label>
              <div className="flex gap-4">
                <div
                  onClick={() => document.getElementById('cover-input')?.click()}
                  className={`w-32 h-32 rounded-xl border-2 border-dashed cursor-pointer flex items-center justify-center overflow-hidden ${
                    coverArtPreview 
                      ? 'border-emerald-500/50' 
                      : 'border-zinc-700 hover:border-violet-500/50'
                  }`}
                >
                  {coverArtPreview ? (
                    <img src={coverArtPreview} alt="Cover" className="w-full h-full object-cover" />
                  ) : (
                    <Image className="w-8 h-8 text-zinc-500" />
                  )}
                </div>
                <input
                  id="cover-input"
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleCoverArtChange(e.target.files?.[0])}
                  className="hidden"
                />
                <div className="flex-1">
                  <p className="text-sm text-zinc-400">Upload album art or cover image</p>
                  <p className="text-xs text-zinc-500 mt-1">Recommended: 500x500px, JPG or PNG</p>
                  {coverArt && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleCoverArtChange(null)}
                      className="text-red-400 mt-2"
                    >
                      Remove
                    </Button>
                  )}
                </div>
              </div>
            </div>
            
            <div className="flex gap-4">
              <Button
                variant="outline"
                onClick={() => setStep(1)}
                className="flex-1 border-zinc-700"
              >
                Back
              </Button>
              <Button
                onClick={() => setStep(3)}
                disabled={!audioFile}
                className="flex-1 bg-violet-600 hover:bg-violet-500"
              >
                Continue
              </Button>
            </div>
          </motion.div>
        )}
        
        {/* Step 3: Pricing */}
        {step === 3 && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-6"
          >
            <h2 className="text-lg font-semibold mb-4">Pricing & License</h2>
            
            <div>
              <label className="text-sm text-zinc-400 mb-2 block">Base Price (USD) *</label>
              <div className="relative">
                <DollarSign className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input
                  type="number"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  placeholder="29.99"
                  min="0"
                  step="0.01"
                  className="pl-10 bg-zinc-900/50 border-zinc-800"
                />
              </div>
              <p className="text-xs text-zinc-500 mt-1">Platform fee: 15%</p>
            </div>
            
            <div>
              <label className="text-sm text-zinc-400 mb-3 block">License Type</label>
              <div className="space-y-3">
                {LICENSE_TYPES.map((license) => (
                  <button
                    key={license.id}
                    onClick={() => setLicenseType(license.id)}
                    className={`w-full flex items-center justify-between p-4 rounded-xl border transition-colors ${
                      licenseType === license.id
                        ? 'bg-violet-500/20 border-violet-500/50'
                        : 'bg-zinc-900/50 border-zinc-800 hover:border-zinc-700'
                    }`}
                  >
                    <div className="text-left">
                      <p className="font-medium text-white">{license.name}</p>
                      <p className="text-sm text-zinc-400">{license.description}</p>
                    </div>
                    <div className={`w-5 h-5 rounded-full border-2 ${
                      licenseType === license.id
                        ? 'border-violet-500 bg-violet-500'
                        : 'border-zinc-600'
                    }`}>
                      {licenseType === license.id && (
                        <Check className="w-full h-full text-white p-0.5" />
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>
            
            <div className="border-t border-zinc-800 pt-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="font-medium text-white">Stems Available</p>
                  <p className="text-sm text-zinc-400">Offer individual track stems</p>
                </div>
                <button
                  onClick={() => setStemsAvailable(!stemsAvailable)}
                  className={`w-12 h-6 rounded-full transition-colors ${
                    stemsAvailable ? 'bg-violet-500' : 'bg-zinc-700'
                  }`}
                >
                  <div className={`w-5 h-5 rounded-full bg-white transition-transform ${
                    stemsAvailable ? 'translate-x-6' : 'translate-x-0.5'
                  }`} />
                </button>
              </div>
              
              {stemsAvailable && (
                <div>
                  <label className="text-sm text-zinc-400 mb-2 block">Stems Additional Price</label>
                  <div className="relative">
                    <DollarSign className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                    <Input
                      type="number"
                      value={stemsPrice}
                      onChange={(e) => setStemsPrice(e.target.value)}
                      placeholder="49.99"
                      min="0"
                      step="0.01"
                      className="pl-10 bg-zinc-900/50 border-zinc-800"
                    />
                  </div>
                </div>
              )}
            </div>
            
            {/* Summary */}
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
              <h3 className="font-medium text-white mb-3">Summary</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-zinc-400">Title</span>
                  <span className="text-white">{title}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-400">Genre</span>
                  <span className="text-white">{genre}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-400">Base Price</span>
                  <span className="text-white">${price || '0.00'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-400">Your Earnings (85%)</span>
                  <span className="text-emerald-400">${((parseFloat(price) || 0) * 0.85).toFixed(2)}</span>
                </div>
              </div>
            </div>
            
            <div className="flex gap-4">
              <Button
                variant="outline"
                onClick={() => setStep(2)}
                className="flex-1 border-zinc-700"
              >
                Back
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={loading || !price}
                className="flex-1 bg-violet-600 hover:bg-violet-500"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Upload Song
                  </>
                )}
              </Button>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
